import { type ColumnDef, type RowData, type SortingState, useTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { type DataTableColumnMeta, type DataTableFeatures, dataTableFeatures } from './features';

// Classes completas, não montadas: o Tailwind só gera o que encontra escrito no código.
const HIDE_BELOW = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
} as const;

function cellClass(meta: DataTableColumnMeta | undefined) {
  return cn(
    meta?.align === 'right' && 'tabular text-right',
    meta?.hideBelow && HIDE_BELOW[meta.hideBelow],
    meta?.className,
  );
}

/**
 * Tabela do sistema (§30). Toda coluna com valor ordena — no navegador, ou no servidor quando a
 * lista é paginada (`sorting`/`onSortingChange`); coluna só de ação não ordena.
 * busca e filtro ficam na barra da página, fora da tabela, porque em geral vão para o endereço
 * ou para o servidor. Paginação no servidor entra quando uma lista passar do que cabe na tela.
 *
 * `data` e `columns` precisam ser estáveis (estado, `useMemo` ou constante de módulo): um array
 * novo a cada render refaz os modelos da tabela.
 */
export function DataTable<T extends RowData>({
  data,
  columns,
  getRowId,
  initialSorting = [],
  rowClassName,
  caption,
  inset = false,
  scroll = false,
  sorting,
  onSortingChange,
  className,
}: {
  data: T[];
  // `any` é o tipo do valor de cada coluna, diferente em cada uma: é assim que o TanStack tipa
  // uma lista de colunas heterogêneas.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<DataTableFeatures, T, any>[];
  getRowId: (row: T) => string;
  initialSorting?: SortingState;
  rowClassName?: (row: T) => string | undefined;
  /** Descrição para leitor de tela, quando o título da seção não bastar. */
  caption?: ReactNode;
  /**
   * Tabela encostada na borda de um cartão: a primeira e a última coluna ganham o mesmo recuo do
   * cabeçalho do cartão, para o texto alinhar.
   */
  inset?: boolean;
  /** Lista longa dentro de modal ou painel: rola na altura do painel, com o cabeçalho fixo. */
  scroll?: boolean;
  /**
   * Ordenação feita no servidor (lista paginada): a tabela só mostra e pede a ordem; os dados já
   * chegam ordenados. Sem isto, ordena no navegador.
   */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  className?: string;
}) {
  const serverSide = onSortingChange !== undefined;
  const table = useTable({
    features: dataTableFeatures,
    data,
    columns,
    getRowId,
    initialState: { sorting: initialSorting },
    ...(serverSide
      ? {
          manualSorting: true,
          state: { sorting: sorting ?? [] },
          onSortingChange: (updater: SortingState | ((old: SortingState) => SortingState)) =>
            onSortingChange(typeof updater === 'function' ? updater(sorting ?? []) : updater),
        }
      : {}),
    enableSortingRemoval: false,
    // Toda coluna com valor ordena; coluna só de ação (sem acessor) nunca. Vazio vai para o fim
    // nas duas direções — não é o menor nem o maior valor.
    // `basic` serve a número, booleano e data ISO; texto para pessoas declara `sortFn: 'text'`.
    defaultColumn: { sortUndefined: 'last', sortFn: 'basic' },
  });

  return (
    <Table
      containerClassName={scroll ? 'max-h-panel overflow-y-auto' : undefined}
      className={cn(
        inset &&
          '[&_td:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:first-child]:pl-4 [&_th:last-child]:pr-4',
        className,
      )}
    >
      {caption && <caption className="sr-only">{caption}</caption>}
      <TableHeader className={scroll ? 'sticky top-0 z-10 bg-card' : undefined}>
        {table.getHeaderGroups().map((group) => (
          <TableRow key={group.id}>
            {group.headers.map((header) => {
              const meta = header.column.columnDef.meta;
              const sorted = header.column.getIsSorted();
              const label = header.isPlaceholder ? null : <table.FlexRender header={header} />;
              return (
                <TableHead
                  key={header.id}
                  className={cn('text-muted-foreground', cellClass(meta))}
                  aria-sort={
                    sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined
                  }
                >
                  {header.column.getCanSort() ? (
                    // O recuo negativo devolve o texto do cabeçalho à mesma linha do conteúdo da
                    // coluna: o espaço interno do botão fica só na área de clique.
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        'gap-1',
                        meta?.align === 'right' ? '-mr-3 flex-row-reverse' : '-ml-3',
                      )}
                    >
                      {label}
                      {sorted === 'asc' ? (
                        <ArrowUp aria-hidden className="size-3" />
                      ) : sorted === 'desc' ? (
                        <ArrowDown aria-hidden className="size-3" />
                      ) : (
                        <ArrowUpDown aria-hidden className="size-3 opacity-50" />
                      )}
                    </Button>
                  ) : (
                    label
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id} className={rowClassName?.(row.original)}>
            {row.getAllCells().map((cell) => (
              <TableCell key={cell.id} className={cellClass(cell.column.columnDef.meta)}>
                <table.FlexRender cell={cell} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
